import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { ToolShell } from "@/components/energy/tool";
import { api } from "@/lib/solarApi";

export default function EnergyLearn() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(null);
  const [article, setArticle] = useState(null);

  useEffect(() => {
    api.get("/energy/articles").then((r) => setArticles(r.data.articles)).catch(() => setArticles([]));
  }, []);

  useEffect(() => {
    if (!slug) { setArticle(null); return; }
    api.get(`/energy/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setArticle(false));
  }, [slug]);

  if (slug) {
    return (
      <>
        <Seo title={article ? `${article.title} — BNM Knowledge Center` : "Knowledge Center — BNM"} description={article?.summary || "BNM Energy Knowledge Center"} />
        <ToolShell testid="learn-article" eyebrow="Energy Knowledge Center" title={article ? article.title : "…"} lead={article?.summary}>
          <Link to="/energy/learn" data-testid="article-back" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All articles
          </Link>
          {article === false ? (
            <p className="text-sm text-slate-500">Article not found.</p>
          ) : article ? (
            <article className="rounded-lg border border-slate-200 bg-white p-7 sm:p-10" data-testid="article-body">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-600">{article.category}</p>
              <div className="mt-5 space-y-5">
                {article.body.split("\n").filter(Boolean).map((p, i) => (
                  <p key={i} className={`leading-relaxed ${i === 0 ? "text-lg text-slate-900 font-medium" : "text-base text-slate-600"}`}>{p}</p>
                ))}
              </div>
              <Link to="/energy/engine" data-testid="article-engine-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                Try the Energy Engine <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </article>
          ) : (
            <p className="text-sm text-slate-500">Loading…</p>
          )}
        </ToolShell>
      </>
    );
  }

  return (
    <>
      <Seo title="Energy Knowledge Center — BNM" description="Plain-language energy guides from BNM: solar basics, batteries, inverters, efficiency, and sector guides for businesses, farms, schools and clinics." />
      <ToolShell
        testid="learn-page"
        eyebrow="BNM Energy Knowledge Center"
        title="Understand your energy."
        lead="Plain-language guides written for Sierra Leone — no engineering degree required."
      >
        {!articles ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="articles-grid">
            {articles.map((a) => (
              <Link key={a.slug} to={`/energy/learn/${a.slug}`} data-testid={`article-card-${a.slug}`}
                className="group rounded-lg border border-slate-200 bg-white p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-600">{a.category}</p>
                <h2 className="mt-3 font-display text-lg font-bold tracking-tight text-slate-900 group-hover:text-emerald-800 transition-colors">{a.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.summary}</p>
                <span className="mt-4 inline-block font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-600">Read →</span>
              </Link>
            ))}
          </div>
        )}
      </ToolShell>
    </>
  );
}
